import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, DatePicker, Divider, Modal, Switch, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  NOTIFICATION_STATUS,
  SORT,
} from '../../../common/constants';
import { formatDate } from '../../../common/utils';
import CommonTable from '../../../components/CommonTable';
import SearchComponent from '../../../components/SearchComponent';
import { MARK_NOTIFICATION_AS_READ_ADMIN } from '../graphql/Mutations';
import { ADMIN_NOTIFICATIONS } from '../graphql/Queries';

const { RangePicker } = DatePicker;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialSuggestedToyFilter = {
  limit: 10,
  search: '',
  skip: 0,
};

const initialSuggestedToySort = {
  field: 'createdAt',
  order: 'DESC',
};

const NotificationTable = () => {
  const { dispatch } = useContext(AppContext);
  const [notificationList, setNotificationList] = useState([]);
  const [notificationSearchTerm, setNotificationSearchTerm] = useState('');
  const [isEmptyNotificationList, setIsEmptyNotificationList] = useState(false);
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [notification, setNotification] = useState({});
  const [isMarkAsReadPrompts, setIsMarkAsReadPrompts] = useState(false);
  const [isMarkAsReadLoading, setIsMarkAsReadLoading] = useState(false);
  const [isAllMarkAsRead, setIsAllMarkAsRead] = useState(false);
  const [isAlreadyReadFilter, setIsAlreadyReadFilter] = useState(false);

  const [isNotificationLoading, setIsNotificationLoading] = useState(true);
  const [whereFilter, setWhereFilter] = useState({});
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [finalWhereFilters, setFinalWhereFilters] = useState({});
  const [finalFilterTags, setFinalFilterTags] = useState(null);

  const [adminNotifications] = useLazyQuery(ADMIN_NOTIFICATIONS, {
    onCompleted: (response) => {
      setNotificationList([...response?.adminNotifications?.data]);
      if (
        response?.adminNotifications?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyNotificationList(true);
      } else {
        setIsEmptyNotificationList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.adminNotifications?.count,
      };
      if (!isAlreadyReadFilter) {
        dispatch({
          type: 'SET_NOTIFICATION_COUNT',
          data: response?.adminNotifications?.count,
        });
      }
      setPaginationProp(pagination);
      setIsNotificationLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [markNotificationAsReadAdmin] = useMutation(
    MARK_NOTIFICATION_AS_READ_ADMIN,
    {
      onError: () => {},
    },
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsNotificationLoading(true);
    adminNotifications({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: notificationSearchTerm,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          hasRead: isAlreadyReadFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
  }, [isAlreadyReadFilter]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsNotificationLoading(true);
    setSortedInfo(sorter);
    adminNotifications({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          skip,
          limit: pagination?.pageSize,
          search: notificationSearchTerm,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          hasRead: isAlreadyReadFilter,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setNotificationSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsNotificationLoading(true);
    adminNotifications({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          hasRead: isAlreadyReadFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
  };

  const handleMarkAsRead = async (id) => {
    setIsMarkAsReadLoading(true);
    const notificationIds = notificationList?.map(
      (notificationData) => notificationData?.id,
    );
    const response = await markNotificationAsReadAdmin({
      variables: {
        where: {
          id: isAllMarkAsRead ? notificationIds : [id],
        },
        data: {
          hasRead: true,
        },
      },
    });
    if (response?.data) {
      setIsMarkAsReadLoading(false);
      setIsNotificationLoading(true);
      setIsMarkAsReadPrompts(false);
      adminNotifications({
        variables: {
          filter: {
            ...initialSuggestedToyFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: notificationSearchTerm,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            hasRead: isAlreadyReadFilter,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialSuggestedToySort,
        },
      });
    }
    setIsMarkAsReadLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsNotificationLoading(true);
    adminNotifications({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: notificationSearchTerm,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          hasRead: isAlreadyReadFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setIsNotificationLoading(true);
    adminNotifications({
      variables: {
        filter: {
          ...initialSuggestedToyFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: notificationSearchTerm,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          hasRead: isAlreadyReadFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSuggestedToySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      if (!filterVisible) {
        setFilterTags(finalFilterTags);
        setWhereFilter(finalWhereFilters);
      }
    }
  }, [filterVisible]);

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      sorter: true,
      width: 150,
      align: 'left',
      className: 'max-width-column pointer',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      sortOrder: sortedInfo?.columnKey === 'title' && sortedInfo?.order,
      render: (_, record) => record?.title || '-',
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column table-tooltip-cell',
      render: (_, record) => (
        <Tooltip
          title={record?.description ? record?.description : 'No Description'}
          placement="topLeft"
          trigger="click"
          className="pointer"
        >
          {record?.description || '-'}
        </Tooltip>
      ),
    },

    {
      title: 'RECEIVED ON',
      dataIndex: 'createdAt',
      key: 'createdAt',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: () => NOTIFICATION_STATUS?.UNREAD,
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 100,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      render: (_, record) => (
        <>
          <div className="action-button">
            <Button
              type="primary"
              size="small"
              className="table-action-primary-btn"
              onClick={() => {
                setIsMarkAsReadPrompts(true);
                setNotification(record);
              }}
            >
              Mark as read
            </Button>
          </div>
        </>
      ),
    },
  ]?.filter((item) => {
    if (item?.dataIndex === 'actions' && isAlreadyReadFilter) {
      return item?.hidden;
    }
    return !item?.hidden;
  });

  const locale = {
    emptyText: isEmptyNotificationList ? '' : <span />,
  };

  return (
    <>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isMarkAsReadPrompts}
        onOk={() => handleMarkAsRead(notification?.id)}
        onCancel={() => {
          setIsMarkAsReadPrompts(false);
          setIsAllMarkAsRead(false);
        }}
        okButtonProps={{ loading: isMarkAsReadLoading }}
      >
        Are you sure you want to
        <strong> mark </strong>
        {isAllMarkAsRead ? 'all' : 'this'} notification as read?
      </Modal>
      <div className="d-flex justify-end mb-16 flex-wrap">
        <div className="mr-16 d-flex align-center">
          <h4 className="m-0 mr-8">Already Read</h4>{' '}
          <Switch
            size="small"
            onChange={(checked) => setIsAlreadyReadFilter(checked)}
          />
        </div>
        <div className="d-flex align-center">
          <Button
            type="primary"
            className="add-toy-btn mr-16"
            onClick={() => {
              setIsAllMarkAsRead(true);
              setIsMarkAsReadPrompts(true);
            }}
            disabled={notificationList?.length === 0 || isAlreadyReadFilter}
          >
            Mark all as read
          </Button>
        </div>
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={notificationList || []}
        loading={isNotificationLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default NotificationTable;
